/**
 * Agent Capability Package Definitions
 *
 * Capability packages are curated bundles of tools that enable specific business
 * functions on an agent. Users toggle packages on/off in the agent editor UI —
 * they never see individual tools.
 *
 * Architecture:
 *   Package (toggle on/off) → Tier (opt-in for higher-risk features) → Tool (invisible to user)
 *
 * Configuration is stored in `metadata.capabilities` on the agent entity.
 * This is separate from the legacy `metadata.toolGroups` array (which continues
 * working for REPORTING and API_ACTIONS).
 *
 * Each package maps to a ToolGroup enum value. Tools within a package are either
 * "infrastructure" (always included when the package is on) or assigned to a
 * specific tier that can be individually enabled/disabled.
 */

import { ToolGroup } from '../enums/tool-group.enum';
import type { ToolAudience } from './tool-audience';

// ── Tier Definition ─────────────────────────────────────────────────

export interface CapabilityTierDefinition {
	/** Machine key used in metadata and capabilityTier on tools (e.g., 'lookups') */
	key: string;
	/** Display label for the UI */
	label: string;
	/** Short description shown under the toggle */
	description: string;
	/** Whether this tier is enabled by default when the package is toggled on */
	enabledByDefault: boolean;
	/**
	 * Behavioural guidance appended to the agent's system prompt only when THIS
	 * tier is enabled (in addition to any package-level addendum). Use for rules
	 * that only apply to a higher-risk tier — e.g. the payment-confirmation flow
	 * for "Make payments". Keep it short; it costs prompt budget.
	 */
	systemPromptAddendum?: string;
}

// ── Package Definition ──────────────────────────────────────────────

export interface CapabilityPackageDefinition {
	/** Machine key matching ToolGroup enum value (e.g., 'external_client_service') */
	key: ToolGroup;
	/** Display label for the UI (e.g., 'Customer Account Service') */
	label: string;
	/** Package-level description shown in the editor */
	description: string;
	/** FontAwesome icon name for the UI card (e.g., 'headset') */
	faIcon: string;
	/**
	 * Who this toolset serves (PD-16920). Every member tool must serve at least
	 * these audiences (a spec enforces it), so a toolset fits an agent exactly
	 * when its member tools do. An 'internal' toolset's tools never run without
	 * a signed-in staff user.
	 */
	audiences: Array<ToolAudience>;
	/**
	 * Optional RBAC action the CURRENT USER must hold to use any tool in this
	 * group. When set, the group's tools are neither advertised to nor executable
	 * by a user lacking the action — a categorical, per-user gate layered on top
	 * of group assignment. Use for groups whose tools reach data beyond the
	 * user's normal scope. No toolset sets it today (REPORTING dropped it in
	 * PD-16920 when its raw-SQL tools were removed); the gate is kept for the
	 * next toolset that needs one.
	 */
	requiredRbacAction?: string;
	/**
	 * Behavioural guidance appended to the agent's system prompt whenever this
	 * package is enabled — regardless of what the admin wrote in their own
	 * prompt. Use for rules the package must ALWAYS enforce (e.g. "verify the
	 * caller before any account lookup"). Keep it short; it costs prompt budget.
	 */
	systemPromptAddendum?: string;
	/** Ordered list of configurable tiers within this package */
	tiers: Array<CapabilityTierDefinition>;
}

// ── Metadata Shape ──────────────────────────────────────────────────

/**
 * Shape of `metadata.capabilities` on an agent.
 *
 * - Key = CapabilityPackageDefinition.key (matches ToolGroup value)
 * - Value meanings:
 *   - `true`   — package enabled, all tiers use their `enabledByDefault` values
 *   - object   — package enabled, per-tier overrides (key = tier key, value = boolean)
 *   - `false`  — explicitly disabled (preserved in metadata for re-enabling)
 *   - absent   — never configured
 */
export type AgentCapabilities = Record<
	string,
	true | Record<string, boolean> | false
>;

// ── Package Registry ────────────────────────────────────────────────

/**
 * Reserved tier key for infrastructure tools that are always included
 * when the package is enabled. Tools without a capabilityTier on their
 * decorator are treated as infrastructure.
 */
export const CAPABILITY_TIER_INFRASTRUCTURE = 'infrastructure';

/**
 * All available capability packages. The frontend renders these as toggleable
 * cards in the agent editor. The backend uses them to determine which tools
 * to inject based on the agent's metadata.capabilities configuration.
 */
export const CAPABILITY_PACKAGES: Array<CapabilityPackageDefinition> = [
	{
		key: ToolGroup.EXTERNAL_CLIENT_SERVICE,
		label: 'Customer Account Service',
		description:
			'Allow callers to verify their identity and look up their invoices, orders, and account information.',
		faIcon: 'headset',
		audiences: ['customer'],
		systemPromptAddendum: `You are speaking with an external customer. Account-specific information — invoices, orders, balances, payment status — is private and requires identity verification.

- If the caller asks for ANY account-specific information and has not already been verified on this call, verify them FIRST: collect their company name plus one strong identifier (an invoice number, order number, account number, or an invoice total), then call Verify Client Identity. Do NOT call an account lookup tool for an unverified caller and wait for it to fail — verify proactively.
- A company name alone, or a phone number alone, is never enough — always collect a strong identifier.
- The moment verification succeeds, CONFIRM the account back to the caller by name (e.g. "Thanks — I've got you verified on the [company] account") before continuing, so a wrong match is caught right away.
- For "do I have any open invoices", "what's my balance", or "how much do I owe", use the account summary (a count and total), not a full invoice list. Keep spoken answers short and never read a long list of invoices aloud.`,
		tiers: [
			{
				key: 'lookups',
				label: 'Look up account information',
				description:
					'Invoices, orders, account balance, and payment status for verified callers.',
				enabledByDefault: true,
			},
			{
				key: 'payments',
				label: 'Make payments',
				description:
					'Let verified callers pay an invoice using a payment method already on file. No card or bank details are ever taken over the phone — only saved methods can be used.',
				// OPT-IN, OFF by default: money movement is high-risk, so the admin
				// must deliberately enable it on a customer-facing agent.
				enabledByDefault: false,
				systemPromptAddendum: `If the caller wants to PAY an invoice:
- First list their saved payment methods and let them choose — read each one as its brand/type and last four digits (e.g. "a Visa ending 4242"). NEVER ask for or accept a card number, security code, expiration, or bank account/routing number over the phone; only methods already on file can be used.
- Default the amount to the invoice's full outstanding balance unless the caller asks to pay a different amount.
- Before charging, read back the exact amount, which invoice, and the chosen method (brand + last four) and get an explicit "yes" from the caller. Only then make the payment — never charge without that confirmation.
- If a payment fails, tell the caller plainly and offer to try a different method on file.`,
			},
			// Future tier:
			// { key: 'orderCreation', label: 'Place orders', ... enabledByDefault: false },
		],
	},
	{
		key: ToolGroup.REPORTING,
		label: 'Reporting',
		description:
			"Answer sales and account questions from the reports, and show the results as report views (tables, charts and metric cards) built from the user's own data access.",
		faIcon: 'chart-line',
		// PD-16920: no direct database queries. The members are render_view
		// (tagged on its decorator) and the report tools listed in
		// TOOL_GROUP_MEMBERSHIP below. Each report tool declares its own
		// report RBAC action and scopes rows to what the user may see, and
		// render_view's data is fetched by the browser through the API with
		// the user's own token, so every data call is already permission
		// checked. That is why this toolset carries no requiredRbacAction: the
		// old categorical gate existed only because the removed SQL tools read
		// the whole tenant unscoped. Internal only, because every member runs
		// as the signed-in user.
		audiences: ['internal'],
		systemPromptAddendum: `## Reporting

- Answer figures with the report tools, never from memory: totals for a period → Sales Total; sales by category, manufacturer, brand or product across clients → Product Sales Summary; one client's purchases, top items, trends or spend in a window → Client Analytics; a list of the user's orders ("my recent orders", "orders this week") → Order Search, not a client search. Each one covers what the user is allowed to see, so describe the result as "your clients" or "the clients you have access to", not the whole company, unless the result says it is company-wide.
- "Show me", "build a report", "chart", "table" → Render View (in the app only; on a call, read the answer out instead): read the data with the tool that answers the question, then show it as a report view of tables, charts and metric cards. Its data sources are API actions in domain.action form (for example clients.search or orders.search); use only actions you know exist, and when Get Action Schema is available, check an action's parameters with it first. Render View shows the view to the user straight away; it is not saved, so do not call it a saved report or promise it will be there later.
- You cannot query the database directly or save a report. If the user asks for either, say so in one sentence and offer a report view instead.
- If a report tool says the user lacks access, tell them plainly; do not try to reach the same data another way.`,
		tiers: [],
	},
	{
		key: ToolGroup.API_ACTIONS,
		label: 'API Actions',
		description:
			'Let the agent call CRM API endpoints generically (discover a capability index, then execute actions). Each call runs through the normal API permission checks.',
		faIcon: 'plug',
		// Internal only: each call runs as the signed-in user through the real
		// endpoint's permission checks, so there is nothing to run as without one.
		audiences: ['internal'],
		tiers: [],
	},
	{
		key: ToolGroup.SALES_HELPER,
		label: 'Sales Helper Toolkit',
		description:
			'The internal CRM toolkit (client / order / quote / invoice / task / opportunity lookups and management) the Sales Helper uses.',
		faIcon: 'briefcase',
		// The staff CRM toolkit: it reads and changes tenant data for the
		// signed-in rep.
		audiences: ['internal'],
		// PD-16874 — the prospecting tools self-tag into this group; their
		// rules ride here so any agent given the toolkit gets them. PD-14466 —
		// so does the rule that a multi-item cart add is one batch call.
		systemPromptAddendum: `## Cart

- Two or more items to add, or several cart quantities to set, go in ONE Add Items to Cart call (one cart recalculation), not one Add Item to Cart call per item. Report each item's result: what was added, and any item that was not (no price — ask the user for one; not found).

## Prospecting (new businesses to sell to)

- "Find me good target accounts in <area>", "new prospects near me", "<kind of business> near my stops <day>" → Prospect Search with the area as said (or nearStopsOn for "near my stops <day>"). Read back about five as a short list: the name, then its reason. Mention the ones already in the CRM in a few words, as the result words them ("Lucky Star is already Dana Lee's account"); never offer to open a restricted one.
- Refine with the same tool and the same area: "only Chinese restaurants" → businessType; "show me more" / "next ones" → more: true.
- "Bigger" / "larger" (or "smaller") places: you cannot judge a business's size from Google yet. Say so in a few words and search without a size filter (pass size so the result carries the sentence). Google reviews show how established or popular a place is, never its size or sales; never describe a prospect as big or small from its reviews.
- "Add <name> / the first two / those as leads" → Prospect Add Leads with the prospectIds of the latest result; "not a fit" / "skip that one" → Prospect Dismiss. Both happen straight away, no "are you sure"; say what was done in one line. "Undo that" → Sales Trip Undo.
- When a result has askTheRep, ask exactly that and wait. No profile yet: ask what kinds of businesses are good customers, save the answer with Prospect Target Profile Save (it decides company or personal), then search again; if the rep would rather have a suggestion, Prospect Target Profile with suggest: true, and save it on a yes.`,
		tiers: [],
	},
	{
		key: ToolGroup.SALES_TRIPS,
		label: 'Sales Trips',
		description:
			"Run a rep's sales day: what is on it, what is next and left, and log, skip, miss, move, pin or remove its stops.",
		faIcon: 'route',
		// A rep's own day, so internal like the toolkit it sits beside. No
		// group-level RBAC action: reps hold sales_trips:*:self and leaders :any,
		// which one action cannot express, so each tool declares its own
		// (PD-16755).
		audiences: ['internal'],
		// Why here and not in the agent prompt: the rules belong to these tools,
		// so any agent given the group gets them (PD-15938).
		//
		// PD-16965: the second rule. Asked about a delivery trip and about
		// service orders, the Sales Helper answered from sales days and sales
		// orders ("no sales day planned", "no open service orders"). The tool
		// names are the display names the Extended Tools index lists.
		systemPromptAddendum: `## Sales trips (the rep's day on the road)

- "What's my day / what's next / what's left / what do I have this week", or any question about a day's stops → Sales Trip Day. For another rep ("what does Amelia have today"), pass their name as \`rep\`.
- Sales trips are a rep's account visits only. Delivery trips (trucks, drivers, stops with orders) → Delivery Trip Day; service orders (technician work) → Service Order Search. If none of your tools covers the question, say plainly that you can't see that; never answer it from sales days or sales orders.
- Name the stop for a stop tool the way the rep did: \`account\` (their words) with \`stopDay\` when it is not today, or \`next: true\` for "the next one". No need to read the day first; the tool finds the stop and says if it is not there or is ambiguous (then ask the rep). Use salesTripStopId when a result already gave it. On another rep's day ("her day", after reading it with \`rep\`), pass the same \`rep\` to the stop tools too. Never guess an id.
- "Log the visit at <account>" → Sales Trip Stop Complete first, by account, every time — it finds the stop on today's day. Only when it answers that the account is not on the day, use Client Visit Create (an ad-hoc visit). A visit needs notes, an outcome, or both: pass outcome when the rep's words say what happened (talked to the buyer → spoke_with_buyer; left a catalog or info → left_info; they ordered → order_placed; not interested → not_interested). If the rep said neither, ask what happened first.
- Went but saw no one ("they were closed", "nobody was there", "the owner was out") → Sales Trip Stop Complete with outcome closed or no_one_available; notes are optional then. It logs an attempt: the stop shows visited, but the account stays due for a visit. Never mark that stop skipped.
- Skip means the rep did not go → Sales Trip Stop Outcome with outcome skipped and an outcomeReason, always: ran out of time, running late → ran_out_of_time; they asked to cancel or move it → customer_request; anything else → other, with the rep's words as detail (other needs them). If the rep gave no reason, ask why.
- Building a day: "accounts near me / near <account, City ST or zip> / worth a visit / I haven't seen in 60 days" → Sales Trip Nearby Accounts (not Client Search); read back 3 to 5 names with distance. "Add <accounts> to <day>" → Sales Trip Add Stops (it starts the day if there is none; "the first two" = the first two of the latest Nearby result, by clientId). "Start a day <day> with <accounts>, leave at <time>" → Sales Trip Create Day. "Optimize it / best order" → Sales Trip Optimize. "Which routes are due" → Sales Trip Routes Due; "build <day> from <route>" → Sales Trip Create From Route.
- Repeating visits: "make this recurring", "every Monday and Thursday", "same route week in, week out", "a standing route" → Sales Route Save (fromDay for a day already planned, or the accounts; weekdays, with everyWeeks for every other week). Never copy a day to several dates to make it repeat: one route comes due on all its days. After saving, read back the schedule and the next dates, and offer to build the coming days from it (Sales Trip Create From Route, one call per day). Changing a route (its days, first-visit time, visit lengths, an account on or off, pausing it) → Sales Route Save with the route. "What routes do I have", "how often is my <route>", "who is on <route>" → Sales Route List.
- Time zones: a stop's time is local to where the account is. When a tool asks which clock a time is on ("10 Eastern or 10 Central?"), ask the rep exactly that and pass their answer as clock; never choose for them. Read times back as the result gives them (with AM or PM, never 24-hour), naming the zone only when the result does.
- Several days: "plan next week around <places>, N days" or "spread these accounts over <days>" → Sales Trip Plan Week. It creates the plan at once; read back each day (date, number of stops, about how long, and any day longer than a working day) and the days skipped. "Delete <day>'s day" or "delete the <name> plan" → Sales Trip Delete, at once; "undo that" brings it back.
- Planning around a place with no priorities given ("plan Thursday around Decatur", "plan two days next week near Atlanta Harvest"): check the agenda once before planning. Call Sales Trip Nearby Accounts for that place and day (about as many accounts as the days need), name three or four with each one's "why", and ask once about a focus. Ask it the way the tool's askTheRep puts it, keeping each account's why, e.g. "I'd start with Dewy Pharmacy (sales down 40%), Atlanta Collective (never visited) and Icebox Pharmacy (due to reorder). Any particular focus, like accounts that are slipping, or go with these?" The accounts are the suggestion; the focus is the question; never ask the rep to pick an account as a focus. "Go with those" → Sales Trip Plan Week with those clientIds. A focus → Sales Trip Plan Week straight away with near, the matching filter (slipping, type lead, minSales90, notVisitedDays, notOrderedDays, notOnRoute) and focus in the rep's words; do not check again, "undo that" is there. Plan straight away, with no check, when the request already says what matters, names the accounts, or says "just pick" / "the usual".
- Every stop carries a "reason for the visit". When the rep says what a visit is for ("add Misty Market tomorrow, I'm dropping off samples"), pass it as purpose (Add Stops, Create Day). To change it ("the reason for Misty Market is the fall line", "change the reason for Misty Market on Thursday to …") → Sales Trip Stop Update with account (and stopDay) and visitReason — not Client Note Create; "undo that" reverses it.
- "What am I going into <account> for?" / "why is <account> on my day?" → Sales Trip Day for that day and read that stop's visitReason, then Client Summary for how the account stands now (last order, sales trend, last visit and its note). Answer in two or three sentences, in your own words rather than reading the stored text ("it's on your list because its ordering has slipped: sales are down 12% and it has never had a visit"). Write amounts, percentages and dates normally ($12,340, 12%, September 23): the text keeps the exact figures, and in voice the system speaks them for you. The order of the stops is the driving route, not a ranking: never say a stop is first because it matters most.
- Days can go to the tools as the rep said them ("today", "tomorrow", "thursday" = the coming Thursday, "next thursday" = next week's, "next week") or as YYYY-MM-DD, which a date the rep named ("October 12") must be, with the year you judge they mean; keep the word "next" when the rep says it, and never ask "which Thursday". Times go to the tools as the rep said them, with AM or PM ("10 AM", "2:30 PM").
- No "are you sure?": complete, skip, move, pin or change a stop, and take one off the day, are done straight away — then say what was done in one short sentence (the result's summary is written for that). An acknowledgement before the tool runs says what is about to happen ("Logging that."), never "Done": nothing is done until the result comes back.
- "Undo that", "I made a mistake", "put it back" → Sales Trip Undo. It reverses the last stop change in this conversation; called again, the one before.
- Read a day as a short list: time, then account, in order. Do not read addresses, phone numbers or ids unless asked.
- Business hours: when a result's summary says "Tell the rep these stops are not when the account is open", always say it in the same reply, naming each stop, its time and the account's hours or the closed day ("Heads up: Candler Park Market opens at 8, and you have it at 6:15."), and offer to move it. Never leave it out, even after a change the rep asked for.`,
		tiers: [],
	},
];

/**
 * Central tool-group membership for groups whose member tools do NOT self-tag
 * via the @AiAgentTool `toolGroup` field. A group's runtime membership is the
 * UNION of (tools whose decorator `toolGroup` === key) and the toolTypes listed
 * here.
 *
 * SALES_HELPER's members are otherwise-ungrouped first-class CRM tool handlers,
 * so they are curated here. API_ACTIONS / EXTERNAL_CLIENT_SERVICE self-tag on
 * their decorators and need no manifest entry.
 *
 * NOTE: `render_view` is intentionally absent — it self-tags REPORTING and
 * reaches the Sales Helper via the REPORTING group, not this toolkit.
 */
export const TOOL_GROUP_MEMBERSHIP: Partial<Record<ToolGroup, Array<string>>> =
	{
		// PD-16920: the report tools, beside render_view (which self-tags). They
		// stay in the Sales Helper toolkit too; a tool may belong to several
		// toolsets, and each one checks its own report RBAC action.
		[ToolGroup.REPORTING]: [
			'salesTotal',
			'productSalesSummary',
			'clientAnalytics',
		],
		[ToolGroup.SALES_HELPER]: [
			// Lookups / navigation
			'navigate',
			'clientSearch',
			'clientSummary',
			// PD-16904: a client's Google listing (hours, website) when asked.
			'clientGoogleLookup',
			'clientAnalytics',
			'clientLifecycleStagesList',
			'productSalesSummary',
			'salesTotal',
			// PD-16983: "top 5 customers this year" (Client Search only ranks
			// by the rolling 90-day revenue).
			'topAccounts',
			'cartGet',
			'orderSearch',
			'orderDetail',
			'invoiceSearch',
			'itemSearch',
			'itemDetail',
			'quoteSearch',
			'quoteSummary',
			'taskSearch',
			'userSearch',
			'dayPlanner',
			// PD-16981: purchase orders and vendors, read-only ("which POs are
			// still open?"). Removed 2026-06 because every extra tool cost
			// routing accuracy on gpt-5.4-mini; re-added 2026-10 once the Sales
			// Helper keeps them behind Lookup (its PD-14463 core set) and runs on
			// Claude Haiku 5.5 (PD-16986). Each checks purchase_orders:read /
			// vendors:read, so a rep without purchasing access is refused.
			'vendorSearch',
			'purchaseOrderSearch',
			'purchaseOrderDetail',
			'paymentCalculateSummary',
			'paymentTransactionHistory',
			// PD-16966: read-only delivery trips ("what's left on today's
			// truck") and an account's service orders ("what service is open").
			// Each checks its own RBAC action and is offered only where the
			// tenant's plan has delivery or service management. Both are in
			// the extended tier, so they add no schema to every call where
			// tiering applies.
			'deliveryTripDay',
			// PD-16984: an account's deliveries ("who is delivering to X
			// today", "did they sign"), extended tier like Delivery Trip Day.
			'deliveryStopSearch',
			'serviceOrderSearch',
			// Client management
			'clientCreate',
			'clientUpdate',
			'clientNoteCreate',
			'clientNoteUpdate',
			'clientNoteDelete',
			'clientNoteSearch',
			'clientVisitCreate',
			// PD-16801: a rep fills in or corrects a visit afterwards (takeaways,
			// next steps, marking a planned visit completed). Without it the
			// agent logged a second visit when asked to update the first.
			'clientVisitUpdate',
			'clientVisitSearch',
			// PD-16787: merge a lead into the customer it turned out to be.
			'clientMergeLead',
			// PD-16787: a client's recent visits, calls and notes for call prep.
			'clientInteractions',
			// PD-17064: people (contacts) across customers: find, read, add to a
			// customer (reusing an existing person), and change. Extended tier.
			'contactSearch',
			'contactDetail',
			'contactCreate',
			'contactUpdate',
			// PD-17066: competitors, and who a customer buys from besides us.
			// Extended tier.
			'competitorSearch',
			'competitorDetail',
			'customerCompetitorSet',
			'customerCompetitorRemove',
			// PD-17104: opportunity and quote outcomes; recent losses and why;
			// what closes in 30/60/90 days. Extended tier.
			'opportunityOutcomeSet',
			'opportunitiesLost',
			'pipelineClosingSoon',
			// PD-17109: competitors quoting an opportunity. Extended tier.
			'opportunityCompetitorSet',
			// PD-17109: people on an opportunity with their role. Extended tier.
			'opportunityPersonSet',
			// PD-17108: visit debriefs (propose, confirm, save, one Undo), a
			// visit's links, and past visits about a record. Extended tier.
			'visitDebrief',
			'visitDebriefUndo',
			'visitDetail',
			'visitLinkAdd',
			'visitLinkRemove',
			'visitsAbout',
			// PD-17097: jobs (the jobs add-on): find, read, create when asked,
			// and put orders or quotes on one. Extended tier.
			'jobSearch',
			'jobDetail',
			'jobCreate',
			'jobLinkOrders',
			// PD-17109: an opportunity's job, and merging duplicate jobs and
			// opportunities with Undo. Extended tier.
			'opportunityJobLink',
			'jobMerge',
			'jobMergeUndo',
			'opportunityMerge',
			'opportunityMergeUndo',
			// PD-17109: projects (the jobs add-on): find, read, create when
			// asked, and put a customer's bid or job on one. Extended tier.
			'projectSearch',
			'projectDetail',
			'projectCreate',
			'projectLink',
			'projectUpdate',
			// PD-17109: change a job, and the people on it. Extended tier.
			'jobUpdate',
			'jobPersonSet',
			// PD-17109: companies on a project; competitors on a project or a
			// job. Extended tier.
			'projectParticipantSet',
			'projectCompetitorSet',
			'jobCompetitorSet',
			// Cart / shopping
			'cartAddItem',
			// PD-14466: several items in one call, one cart recalculation.
			'cartAddItems',
			'cartUpdateItem',
			'cartRemoveItem',
			'cartClear',
			'cartSetDiscount',
			// Order management
			'orderCreateNote',
			'orderNotesList',
			// Quote management
			'quoteCreate',
			'quoteUpdate',
			'quoteAddItem',
			'quoteUpdateItem',
			'quoteDelete',
			'quoteNoteCreate',
			// Email — internal-only variants (from the system noreply, to the rep
			// themselves) plus the external send that goes FROM the rep's own
			// connected mailbox to anyone (co-worker, contact, customer).
			'emailSendInternal',
			'quoteSendEmailInternal',
			'emailSendExternal',
			// Opportunity / pipeline
			'opportunityCreate',
			'opportunitySearch',
			'opportunityStagesList',
			'opportunityDetail',
			'opportunityUpdate',
			'opportunityUpdateStage',
			'opportunityCreateNote',
			'opportunityNotesList',
			// Issue / ticket management — the subset a rep actually works:
			// file a ticket for a customer problem, find it again, read it, move
			// its status, and leave a note. The full toolkit was dropped 2026-06
			// because every extra tool costs routing accuracy on the mini model;
			// this is the deliberate re-add of the customer-facing half only.
			// Still excluded (and correspondingly not registered as providers in
			// issue.module.ts): issueNoteEdit / issueNoteDelete and the
			// issueWatcher add/list/remove trio — admin-side workflows a rep does
			// in the UI, not through the assistant.
			'issueCreate',
			'issueSearch',
			'issueDetail',
			'issueUpdate',
			'issueStatusUpdate',
			'issueNoteCreate',
			'issueNoteList',
			// Task management
			'taskCreate',
			'taskUpdate',
			'taskDelete',
		],
	};

// ── Helper Functions ────────────────────────────────────────────────

/** Get a package definition by its key */
export function getCapabilityPackage(
	key: string,
): CapabilityPackageDefinition | undefined {
	return CAPABILITY_PACKAGES.find((p) => p.key === key);
}

/**
 * Concatenated system-prompt guidance for every ENABLED package that declares a
 * `systemPromptAddendum`. Appended to the agent's prompt at conversation setup
 * so package behavioural rules hold regardless of the admin's own prompt.
 * Returns '' when no enabled package contributes guidance.
 */
export function getCapabilityPromptAddenda(
	capabilities: AgentCapabilities | undefined,
): string {
	if (capabilities == null) {
		return '';
	}
	const parts: Array<string> = [];
	for (const pkg of CAPABILITY_PACKAGES) {
		if (!isCapabilityPackageEnabled(capabilities, pkg.key)) {
			continue;
		}
		if (pkg.systemPromptAddendum != null) {
			parts.push(pkg.systemPromptAddendum);
		}
		// Tier-level guidance is included only when that tier is enabled (e.g. the
		// payment-confirmation flow appears only once "Make payments" is on).
		for (const tier of pkg.tiers) {
			if (
				tier.systemPromptAddendum != null &&
				isCapabilityTierEnabled(capabilities, pkg.key, tier.key)
			) {
				parts.push(tier.systemPromptAddendum);
			}
		}
	}
	return parts.join('\n\n');
}

/**
 * The packages whose prompt guidance applies to an agent (PD-16920): those
 * enabled in its capabilities plus the toolsets listed in its toolGroups, the
 * same for every agent. A toolset reached through toolGroups brings its
 * guidance with all tiers at their defaults; an explicit capabilities entry
 * for the same package wins, including `false`.
 */
export function getPromptAddendaCapabilities(
	capabilities: AgentCapabilities | undefined,
	toolGroups: ReadonlyArray<string> | undefined,
): AgentCapabilities | undefined {
	if (toolGroups == null || toolGroups.length === 0) {
		return capabilities;
	}
	return {
		...Object.fromEntries(toolGroups.map((group) => [group, true as const])),
		...(capabilities ?? {}),
	};
}

/** Check whether a package is enabled at all (any value other than false/absent) */
export function isCapabilityPackageEnabled(
	capabilities: AgentCapabilities | undefined,
	packageKey: string,
): boolean {
	if (capabilities == null) {
		return false;
	}
	const pkg = capabilities[packageKey];
	return pkg != null && pkg !== false;
}

/**
 * Check whether a specific tier is enabled within a package.
 *
 * Resolution order:
 * 1. If package is disabled → false
 * 2. If package is `true` → use the tier's `enabledByDefault`
 * 3. If package is an object → check the tier key in the object,
 *    falling back to `enabledByDefault` if the tier key is absent
 */
export function isCapabilityTierEnabled(
	capabilities: AgentCapabilities | undefined,
	packageKey: string,
	tierKey: string,
): boolean {
	if (capabilities == null) {
		return false;
	}

	const pkg = capabilities[packageKey];

	// Package disabled or absent
	if (pkg == null || pkg === false) {
		return false;
	}

	// Package enabled with all defaults
	if (pkg === true) {
		const def = getCapabilityPackage(packageKey);
		const tier = def?.tiers.find((t) => t.key === tierKey);
		return tier?.enabledByDefault ?? false;
	}

	// Package enabled with tier overrides
	const tierVal = pkg[tierKey];
	if (tierVal != null) {
		return tierVal;
	}

	// Tier not specified in overrides — fall back to default
	const def = getCapabilityPackage(packageKey);
	const tier = def?.tiers.find((t) => t.key === tierKey);
	return tier?.enabledByDefault ?? false;
}
